import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Slot } from "@radix-ui/react-slot";
import clsx from "clsx";
import { createPluggable } from "pluggable-react";
import React, { createContext, useCallback, useContext } from "react";
import { withRefProp } from "../../../utils/withRefProp";
import {
  type PersistentComponent,
  type PersistentComponentLayer,
  type PersistentComponentProps,
  usePersistentComponents,
} from "../../blocks/PersistentComponents/PersistentComponents";
import Button, { type ButtonProps } from "../Button/Button";
import { DismissableProvider, useDismissable } from "../DismissGuard/DismissGuard";
import Modal, {
  type ModalProps,
  type ModalCloseOptions,
  type ModalCloseProps,
  type ModalContentProps,
  type ModalControlsProps,
  type ModalOverlayProps,
  type ModalTriggerProps,
  useModalContext,
} from "../Modal/Modal";
import { OverlayDepthContext, useOverlayDepth } from "../OverlayDepth";
import styles from "./Dialog.module.scss";

// Re-export context hook
export { useModalContext as useDialogContext };

// Dialog is a Modal with default dialog-like styling
export type DialogIntent = "compact" | "form" | "cover";

export interface DialogProps extends ModalProps {
  intent?: DialogIntent;
}

export type PushDialogOptions<P = any> = {
  layer: PersistentComponentLayer;
  component: PersistentComponent<P>;
  options?: Omit<DialogProps, "children" | "open">;
};

export interface PopDialogOptions {
  layer: PersistentComponentLayer;
}

const DialogIntentContext = createContext<DialogIntent>("compact");
const DialogInlineModeContext = createContext<boolean>(false);

interface DialogDismissRootProps {
  onOpenChange?: DialogProps["onOpenChange"];
  children: (handleOpenChange: NonNullable<DialogProps["onOpenChange"]>) => React.ReactNode;
}

const DialogDismissRoot = ({ onOpenChange, children }: DialogDismissRootProps) => {
  const { dismiss } = useDismissable();
  const emitDialogClose = useCallback((options?: ModalCloseOptions) => {
    if (typeof window === "undefined") return;
    if (process.env.NODE_ENV !== "test") return;
    window.dispatchEvent(
      new window.CustomEvent("deci:dialog:close", {
        detail: { options: options ?? null },
      }),
    );
  }, []);

  const handleOpenChange = useCallback(
    (open: boolean, options?: ModalCloseOptions) => {
      if (!open) {
        if (options?.type === "success") {
          emitDialogClose(options);
          onOpenChange?.(false, options);
          return;
        }
        dismiss(() => {
          emitDialogClose(options);
          onOpenChange?.(false);
        });
      } else {
        onOpenChange?.(true);
      }
    },
    [dismiss, emitDialogClose, onOpenChange],
  );

  return children(handleOpenChange);
};

const DefaultDialog = ({ intent = "form", children, onOpenChange, ...props }: DialogProps) => {
  return (
    <DismissableProvider>
      <DialogIntentContext.Provider value={intent}>
        <DialogDismissRoot onOpenChange={onOpenChange}>
          {(handleOpenChange) => (
            <Modal
              {...props}
              onOpenChange={handleOpenChange}
            >
              {children}
            </Modal>
          )}
        </DialogDismissRoot>
      </DialogIntentContext.Provider>
    </DismissableProvider>
  );
};

const Dialog = createPluggable<DialogProps>("deci.dialog", DefaultDialog);

// Content with dialog styling (background, shadow, padding)
export type DialogContentProps = ModalContentProps;

export interface DialogContentPropsWithRef extends DialogContentProps {
  ref?: React.Ref<HTMLDivElement>;
}

const DefaultDialogContent = withRefProp(({ className, style, ref, children, ...props }: DialogContentPropsWithRef) => {
  const intent = useContext(DialogIntentContext);
  const inline = useContext(DialogInlineModeContext);

  if (inline) {
    return (
      <div
        ref={ref}
        data-intent={intent}
        className={clsx(styles.content, className)}
        style={style}
        {...(props as React.HTMLAttributes<HTMLDivElement>)}
      >
        {children}
      </div>
    );
  }

  return (
    <Modal.Content
      ref={ref}
      data-intent={intent}
      className={clsx(styles.content, className)}
      style={style}
      {...props}
    >
      {children}
    </Modal.Content>
  );
});

const DialogContent = createPluggable<DialogContentProps>("deci.dialog.content", DefaultDialogContent);

// Header
export type DialogHeaderProps = React.HTMLAttributes<HTMLDivElement>;

const DefaultDialogHeader = ({ className, ...props }: DialogHeaderProps) => (
  <div
    className={clsx(styles.header, className)}
    {...props}
  />
);

const DialogHeader = createPluggable<DialogHeaderProps>("deci.dialog.header", DefaultDialogHeader);

// Title
export type DialogTitleProps = React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>;

export interface DialogTitlePropsWithRef extends DialogTitleProps {
  ref?: React.Ref<React.ElementRef<typeof DialogPrimitive.Title>>;
}

const DefaultDialogTitle = withRefProp(({ className, ref, ...props }: DialogTitlePropsWithRef) => (
  <DialogPrimitive.Title
    ref={ref}
    className={clsx(styles.title, className)}
    {...props}
  />
));

const DialogTitle = createPluggable<DialogTitleProps>("deci.dialog.title", DefaultDialogTitle);

// Description
export type DialogDescriptionProps = React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>;

export interface DialogDescriptionPropsWithRef extends DialogDescriptionProps {
  ref?: React.Ref<React.ElementRef<typeof DialogPrimitive.Description>>;
}

const DefaultDialogDescription = withRefProp(({ className, ref, ...props }: DialogDescriptionPropsWithRef) => (
  <DialogPrimitive.Description
    ref={ref}
    className={clsx(styles.description, className)}
    {...props}
  />
));

const DialogDescription = createPluggable<DialogDescriptionProps>("deci.dialog.description", DefaultDialogDescription);

// Body
export type DialogBodyProps = React.HTMLAttributes<HTMLDivElement> & {
  asChild?: boolean;
};

const DefaultDialogBody = (props: DialogBodyProps) => {
  const { className, asChild, ...otherProps } = props;
  const Component = asChild ? Slot : "div";
  return React.createElement(Component, { className: clsx(styles.body, className), ...otherProps });
};

const DialogBody = createPluggable<DialogBodyProps>("deci.dialog.body", DefaultDialogBody);

// Controls
export type DialogControlsProps = ModalControlsProps;

const DialogControls = createPluggable<DialogControlsProps>("deci.dialog.controls", Modal.Controls);

export interface DialogInlineRootProps {
  intent?: DialogIntent;
  children: React.ReactNode;
}

const DefaultDialogInlineRoot = ({ intent = "form", children }: DialogInlineRootProps) => (
  <DialogInlineModeContext.Provider value={true}>
    <DialogIntentContext.Provider value={intent}>
      <DialogPrimitive.Root open>{children}</DialogPrimitive.Root>
    </DialogIntentContext.Provider>
  </DialogInlineModeContext.Provider>
);

const DialogInlineRoot = createPluggable<DialogInlineRootProps>("deci.dialog.inline-root", DefaultDialogInlineRoot);

// Action buttons
export type DialogActionButtonProps = Omit<ButtonProps, "variant">;

const DefaultDialogActionsPrimaryButton = (props: DialogActionButtonProps) => (
  <Button
    intent={"primary"}
    {...props}
  />
);

const DialogActionsPrimaryButton = createPluggable<DialogActionButtonProps>(
  "deci.dialog.actions.primary-button",
  DefaultDialogActionsPrimaryButton,
);

const DefaultDialogActionsSecondaryButton = (props: DialogActionButtonProps) => <Button {...props} />;

const DialogActionsSecondaryButton = createPluggable<DialogActionButtonProps>(
  "deci.dialog.actions.secondary-button",
  DefaultDialogActionsSecondaryButton,
);

const DefaultDialogActionsCancelButton = ({
  onClick,
  children = "Cancel",
  label = "Cancel",
  ...props
}: DialogActionButtonProps) => {
  const modalContext = useModalContext();

  return (
    <DialogActionsSecondaryButton
      {...props}
      label={label}
      onClick={(event) => {
        onClick?.(event);
        if (!event.defaultPrevented) {
          modalContext?.close();
        }
      }}
    >
      {children}
    </DialogActionsSecondaryButton>
  );
};

const DialogActionsCancelButton = createPluggable<DialogActionButtonProps>(
  "deci.dialog.actions.cancel-button",
  DefaultDialogActionsCancelButton,
);

// Actions
export type DialogActionsProps = React.HTMLAttributes<HTMLDivElement>;

const DefaultDialogActions = ({ className, ...props }: DialogActionsProps) => (
  <div
    className={clsx(styles.actions, className)}
    {...props}
  />
);

const DialogActions = createPluggable<DialogActionsProps>("deci.dialog.actions", DefaultDialogActions);

// Re-export types
export type DialogTriggerProps = ModalTriggerProps;
export type DialogOverlayProps = ModalOverlayProps;
export type DialogCloseProps = ModalCloseProps;

export const useDialog = () => {
  const { pushComponent, popComponent } = usePersistentComponents();
  // Captured at the call site so the dialog stacks above whatever overlay it was
  // triggered from, rather than the ambient depth where PersistentComponents happens to be mounted.
  const depth = useOverlayDepth();

  const pushDialog = useCallback(
    <P = any>({ layer, component, options }: PushDialogOptions<P>) => {
      const { onOpenChange, ...dialogProps } = options || {};

      const WrappedDialog: PersistentComponent<P> = (componentProps: PersistentComponentProps<P>) => (
        <OverlayDepthContext.Provider value={depth}>
          <Dialog
            open={true}
            onOpenChange={(open) => {
              onOpenChange?.(open);
              if (!open) {
                componentProps.close();
              }
            }}
            {...dialogProps}
          >
            {React.createElement(component, componentProps)}
          </Dialog>
        </OverlayDepthContext.Provider>
      );

      return pushComponent({ layer, component: WrappedDialog });
    },
    [pushComponent, depth],
  );

  const popDialog = useCallback(
    ({ layer }: PopDialogOptions) => {
      popComponent({ layer });
    },
    [popComponent],
  );

  return { pushDialog, popDialog };
};

export default Object.assign(Dialog, {
  Trigger: Modal.Trigger,
  Portal: Modal.Portal,
  Overlay: Modal.Overlay,
  Close: Modal.Close,
  Content: DialogContent,
  Header: DialogHeader,
  Title: DialogTitle,
  Description: DialogDescription,
  Body: DialogBody,
  Controls: DialogControls,
  /**
   * Provides the Dialog and Radix contexts without mounting any overlay or portal.
   *
   * Use this to render `Dialog` sub-components (e.g. `Dialog.Title`, `Dialog.Description`,
   * `Dialog.Controls`) in static or inline contexts such as design system documentation,
   * previews, or unit tests — anywhere a full modal is not appropriate.
   */
  InlineRoot: DialogInlineRoot,
  Actions: Object.assign(DialogActions, {
    PrimaryButton: DialogActionsPrimaryButton,
    SecondaryButton: DialogActionsSecondaryButton,
    CancelButton: DialogActionsCancelButton,
  }),
});
